import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, query, orderBy, limit, startAfter } from 'firebase/firestore';
import {
  canonicalizeStateToken,
  normalizeLocationText,
  parseCityStateQuery,
  statesMatch,
} from '../utils/searchUtils';

const VenueContext = createContext();

export const filterVenuesByLocation = (venues, location) => {
  if (!location) return venues;

  const parsed = parseCityStateQuery(location) || (() => {
    const parts = String(location).split(',').map((part) => part.trim()).filter(Boolean);
    if (!parts.length) return null;
    const city = normalizeLocationText(parts[0].replace(/^(the\s+|city\s+of\s+)/i, ''));
    const statePart = parts[1] ? parts[1].replace(/^(state\s+of\s+)/i, '') : '';
    const state = canonicalizeStateToken(statePart) || normalizeLocationText(statePart) || null;
    return { city, state, stateRaw: normalizeLocationText(statePart) || null };
  })();

  if (!parsed?.city && !parsed?.state) return venues;

  const cleanSearchCity = parsed.city || '';
  const cleanSearchState = parsed.state || parsed.stateRaw || '';

  // Try exact city and state match first
  let filteredVenues = venues.filter((venue) => {
    const venueCity = normalizeLocationText(venue.city);
    const cityMatch = venueCity === cleanSearchCity;
    const stateMatch = !cleanSearchState || statesMatch(venue.state, cleanSearchState);
    return cityMatch && stateMatch;
  });

  // If no exact matches, try partial city matches
  if (filteredVenues.length === 0) {
    filteredVenues = venues.filter((venue) => {
      const venueCity = normalizeLocationText(venue.city);

      const cityMatch =
        venueCity?.includes(cleanSearchCity) ||
        cleanSearchCity?.includes(venueCity) ||
        cleanSearchCity.split(/\s+/).some((word) => word.length > 2 && venueCity?.includes(word));

      const stateMatch = !cleanSearchState || statesMatch(venue.state, cleanSearchState);

      return cityMatch && stateMatch;
    });
  }

  // If still no matches and state is provided, try state matches
  if (filteredVenues.length === 0 && cleanSearchState) {
    filteredVenues = venues.filter((venue) => statesMatch(venue.state, cleanSearchState));
  }

  // If still no matches, try very lenient matching
  if (filteredVenues.length === 0) {
    filteredVenues = venues.filter((venue) => {
      const venueCity = normalizeLocationText(venue.city);
      const venueState = normalizeLocationText(venue.state);
      const searchWords = cleanSearchCity.split(/\s+/).filter((word) => word.length > 2);

      return searchWords.some((word) =>
        venueCity?.includes(word) ||
        venueState?.includes(word) ||
        statesMatch(venue.state, word)
      );
    });
  }

  return filteredVenues;
};

export const filterVenuesBySearch = (venues, searchQuery) => {
  if (!searchQuery) return venues;
  
  const search = searchQuery.toLowerCase().trim();
  
  // First try exact name matches
  let filteredVenues = venues.filter(venue => 
    venue.name?.toLowerCase().trim() === search
  );

  // If no exact matches, try partial name matches
  if (filteredVenues.length <= 1) {
    filteredVenues = venues.filter(venue => {
      const venueName = venue.name?.toLowerCase().trim();
      const venueCity = venue.city?.toLowerCase().trim();
      const venueState = venue.state?.toLowerCase().trim();
      
      // Check for matches in name, city, or state
      return (
        venueName?.includes(search) ||
        search?.includes(venueName) ||
        venueCity?.includes(search) ||
        venueState?.includes(search)
      );
    });
  }

  // If still very few results, try more lenient matching
  if (filteredVenues.length <= 1) {
    filteredVenues = venues.filter(venue => {
      const venueName = venue.name?.toLowerCase().trim();
      const venueCity = venue.city?.toLowerCase().trim();
      const venueState = venue.state?.toLowerCase().trim();
      
      // Split search into words and check if any word matches
      const searchWords = search.split(' ');
      return searchWords.some(word => 
        venueName?.includes(word) ||
        venueCity?.includes(word) ||
        venueState?.includes(word)
      );
    });
  }

  return filteredVenues;
};

export function VenueProvider({ children }) {
  const [venues, setVenues] = useState([]);
  const [archivedVenues, setArchivedVenues] = useState([]);
  const [allVenues, setAllVenues] = useState([]); // Keep track of all venues for search/filter
  const [loading, setLoading] = useState(true);
  const [hasMoreVenues, setHasMoreVenues] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadAllVenuesForSearch = async () => {
    try {
      const db = getFirestore();
      const allVenuesSnapshot = await getDocs(collection(db, 'venues'));
      const allVenuesData = allVenuesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllVenues(allVenuesData);
      return allVenuesData;
    } catch (error) {
      console.error('Error loading all venues:', error);
      return [];
    }
  };

  const loadVenues = async (isLoadingMore = false) => {
    try {
      if (!isLoadingMore) {
        setLoading(true);
        // Load all venues for search/filtering but only display first 30
        const allVenuesData = await loadAllVenuesForSearch();
        console.log('Loaded all venues for search:', allVenuesData.length);
        setVenues(allVenuesData);
        // setHasMoreVenues(allVenuesData.length > 30);
      } else {
        setLoadingMore(true);
        // Load more from the allVenues array
        const currentLength = venues.length;
        const nextBatch = allVenues.slice(currentLength, currentLength + 30);
        setVenues(prevVenues => [...prevVenues, ...nextBatch]);
        setHasMoreVenues(currentLength + 30 < allVenues.length);
      }
    } catch (error) {
      console.error('Error loading venues:', error);
      if (!isLoadingMore) {
        setVenues([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadArchivedVenues = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const archivedVenuesSnapshot = await getDocs(collection(db, 'archived_venues'));
      const archivedVenuesData = archivedVenuesSnapshot.docs.map(doc => ({
        id: doc.id, 
        ...doc.data()
      }));
      setArchivedVenues(archivedVenuesData);
    } catch (error) {
      console.error('Error loading archived venues:', error);
      setArchivedVenues([]);  
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVenues();
    loadArchivedVenues();
  }, []);

  const loadMoreVenues = () => {
    if (hasMoreVenues && !loadingMore) {
      loadVenues(true);
    }
  };

  const refreshVenues = () => {
    setHasMoreVenues(true);
    loadVenues();
    loadArchivedVenues();
  };

  return (
    <VenueContext.Provider value={{ 
      venues, 
      allVenues, // Provide access to all venues for search/filter
      archivedVenues,
      loading, 
      loadingMore,
      hasMoreVenues,
      refreshVenues,
      loadMoreVenues,
      filterVenuesByLocation,
      filterVenuesBySearch
    }}>
      {children}
    </VenueContext.Provider>
  );
}

export const useVenues = () => {
  const context = useContext(VenueContext);
  if (!context) {
    throw new Error('useVenues must be used within a VenueProvider');
  }
  return context;
}; 